<template>
  <div class="role-management-container">
    <a-card title="角色管理" :bordered="false">
      <div slot="extra">
        <a-tag v-if="currentTenantInfo" color="blue">
          当前租户: {{ currentTenantInfo.name }} (ID: {{ currentTenantInfo.id }})
        </a-tag>
        <a-button type="primary" @click="openCreateDialog" icon="plus" style="margin-left: 10px;">新增角色</a-button>
        <a-button style="margin-left: 10px;" @click="loadRoles" icon="reload">刷新</a-button>
      </div>

      <!-- 角色列表 -->
      <a-table
        :columns="columns"
        :data-source="roles"
        :loading="loading"
        :pagination="pagination"
        rowKey="id"
        @change="handleTableChange">
        <span slot="permissions" slot-scope="permissions">
          <a-tag v-for="perm in permissions.slice(0, 3)" :key="perm" color="blue">
            {{ perm }}
          </a-tag>
          <a-tag v-if="permissions.length > 3" color="blue">
            +{{ permissions.length - 3 }} 更多
          </a-tag>
        </span>
        <span slot="actions" slot-scope="text, record">
          <a @click="openEditDialog(record)">编辑</a>
          <a-divider type="vertical" />
          <a-popconfirm
            title="确定要删除这个角色吗？"
            @confirm="deleteRole(record)"
            okText="确定"
            cancelText="取消">
            <a href="#" style="color: red;">删除</a>
          </a-popconfirm>
        </span>
      </a-table>

      <!-- 角色编辑对话框 -->
      <a-modal
        :title="dialogTitle"
        :visible="dialogVisible"
        @ok="saveRole"
        @cancel="closeDialog"
        width="800px">
        <a-form :form="roleForm" :label-col="{ span: 6 }" :wrapper-col="{ span: 18 }">
          <a-form-item label="角色名称" :validate-status="getFieldError('name') ? 'error' : ''" :help="getFieldError('name') || ''">
            <a-input
              v-decorator="[
                'name',
                { rules: [{ required: true, message: '请输入角色名称' }] }
              ]"
              placeholder="请输入角色名称" />
          </a-form-item>
          
          <a-form-item label="角色编码" :validate-status="getFieldError('code') ? 'error' : ''" :help="getFieldError('code') || ''">
            <a-input-number
              v-decorator="[
                'code',
                { 
                  rules: [
                    { required: true, message: '请输入角色编码' },
                    { type: 'number', message: '角色编码必须为数字' }
                  ]
                }
              ]"
              :min="1"
              style="width: 100%"
              placeholder="请输入角色编码" />
          </a-form-item>

          <a-form-item label="权限分配">
            <!-- 提示信息 -->
            <a-alert 
              v-if="isSuperAdmin" 
              type="info" 
              show-icon
              style="margin-bottom: 16px;"
            >
              <template slot="message">
                超级管理员可以分配系统中的所有权限
                <a-button type="link" size="small" @click="showAllPermissions">查看全部权限</a-button>
              </template>
            </a-alert>
            
            <!-- 租户用户提示信息 -->
            <a-alert 
              v-else 
              type="info" 
              show-icon
              style="margin-bottom: 16px;"
            >
              <template slot="message">
                当前租户可用权限：{{ tenantPermissions.length }} 项（仅可查看，不可修改）
                <a-button type="link" size="small" @click="showTenantPermissions">查看详情</a-button>
              </template>
            </a-alert>
                       
            <!-- 权限树 -->
            <a-tree
              v-if="permissionTreeData.length > 0"
              v-model="checkedPermissionKeys"
              checkable
              :tree-data="permissionTreeData"
              :expanded-keys="permissionExpandedKeys"
              :replace-fields="{ title: 'label', key: 'value', children: 'children' }"
              :checkable="isSuperAdmin"
              @expand="handlePermissionTreeExpand">
            </a-tree>
            
            <div v-else style="text-align: center; padding: 20px; color: #999;">
              {{ isSuperAdmin ? '系统中暂无可用权限' : '当前租户暂无可用权限' }}
            </div>
          </a-form-item>
        </a-form>
      </a-modal>

      <!-- 租户权限详情弹窗 -->
      <a-modal
        :visible="tenantPermissionsVisible"
        title="租户可用权限详情"
        @cancel="tenantPermissionsVisible = false"
        :footer="null"
        width="600px">
        <div style="max-height: 400px; overflow-y: auto;">
          <a-list
            size="small"
            :data-source="tenantPermissions"
            :pagination="false">
            <a-list-item slot="renderItem" slot-scope="item">
              <a-tag>{{ item }}</a-tag>
            </a-list-item>
          </a-list>
        </div>
      </a-modal>
      
      <!-- 全部权限详情弹窗（超管使用） -->
      <a-modal
        :visible="allPermissionsVisible"
        title="系统全部权限详情"
        @cancel="allPermissionsVisible = false"
        :footer="null"
        width="600px">
        <div style="max-height: 400px; overflow-y: auto;">
          <a-tree
            :tree-data="permissionTreeData"
            :replace-fields="{ title: 'label', key: 'value', children: 'children' }">
          </a-tree>
        </div>
      </a-modal>
    </a-card>
  </div>
</template>

<script>
export default {
  name: 'RoleManagement',
  data() {
    return {
      roleForm: this.$form.createForm(this),
      roles: [],
      loading: false,
      checkedPermissionKeys: [],
      permissionExpandedKeys: [],
      permissionTreeData: [],
      tenantPermissions: [], // 当前租户的权限列表
      tenantPermissionsVisible: false, // 租户权限详情弹窗显示状态
      allPermissionsVisible: false, // 全部权限详情弹窗显示状态
      isSuperAdmin: false, // 是否为超级管理员
      
      columns: [
        { title: 'ID', dataIndex: 'id', width: 80 },
        { title: '角色名称', dataIndex: 'name', width: 150 },
        { title: '角色编码', dataIndex: 'code', width: 100 },
        { title: '权限数量', dataIndex: 'permissions', width: 120, scopedSlots: { customRender: 'permissions' } },
        { title: '描述', dataIndex: 'description', width: 200 },
        { title: '创建时间', dataIndex: 'created_at', width: 160 },
        { title: '操作', width: 150, scopedSlots: { customRender: 'actions' } }
      ],
      
      pagination: {
        current: 1,
        pageSize: 10,
        total: 0,
        showSizeChanger: true,
        showQuickJumper: true,
        pageSizeOptions: ['10', '20', '50', '100']
      },
      
      dialogVisible: false,
      dialogTitle: '',
      currentRole: {}
    }
  },
  
  async mounted() {
    await this.checkUserRole();
    await this.loadRoles();
    // 加载所有可用权限（后端会根据用户类型返回不同的结果）
    await this.loadAvailablePermissions();
    // 如果是租户用户，还需要加载租户权限
    if (!this.isSuperAdmin) {
      await this.loadTenantPermissions();
    }
  },
  
  methods: {
    // 检查用户角色
    async checkUserRole() {
      try {
        const userInfo = JSON.parse(localStorage.getItem('aoxy-user') || '{}');
        console.log('[角色管理] localStorage 用户信息:', userInfo);
            
        // 根据 role.code 判断是否为超级管理员（code=100 为超管）
        this.isSuperAdmin = userInfo.role && userInfo.role.code === 100;
        console.log('[角色管理] 检查用户角色:', {
          isSuperAdmin: this.isSuperAdmin,
          role_code: userInfo.role?.code
        });
      } catch (error) {
        console.error('检查用户角色失败:', error);
        this.isSuperAdmin = false;
      }
    },
        
    async loadRoles() {
      this.loading = true;
      try {
        const response = await this.$api.get_roles({
          page_no: this.pagination.current,
          page_size: this.pagination.pageSize
        });
        this.roles = response.data || [];
        this.pagination.total = response.page?.total || this.roles.length;
      } catch (error) {
        console.error('加载角色列表失败:', error);
        this.$message.error('加载角色列表失败');
      } finally {
        this.loading = false;
      }
    },
    
    // 加载可用权限（根据用户类型返回不同的权限列表）
    async loadAvailablePermissions() {
      try {
        console.log('[角色管理] 开始加载可用权限，当前用户类型:', this.isSuperAdmin ? '超级管理员' : '租户用户');
        
        // 使用新接口获取权限树（后端会根据用户类型返回不同的结果）
        const response = await this.$api.get_available_permissions_tree();
        if (response.code === 200) {
          this.permissionTreeData = response.data || [];
          this.permissionExpandedKeys = this.getAllPermissionKeys(this.permissionTreeData);
          
          console.log('[角色管理] 加载可用权限成功，权限树节点数量:', this.permissionTreeData.length);
          
          // 打印所有权限值用于调试
          const allPermissionValues = [];
          const collectValues = (nodes) => {
            nodes.forEach(node => {
              if (node.value) allPermissionValues.push(node.value);
              if (node.children && node.children.length) {
                collectValues(node.children);
              }
            });
          };
          collectValues(this.permissionTreeData);
          console.log('[角色管理] 所有可用权限值:', allPermissionValues);
        } else {
          console.error('[角色管理] 加载可用权限失败，响应码:', response.code);
        }
      } catch (error) {
        console.error('[角色管理] 加载可用权限失败:', error);
        this.$message.error('加载可用权限失败');
      }
    },
    
    async loadPermissionTree() {
      try {
        const response = await this.$api.getPermissionTree();
        // 如果是租户用户，需要根据租户权限过滤
        if (!this.isSuperAdmin && this.tenantPermissions.length > 0) {
          this.permissionTreeData = this.filterPermissionTreeByTenant(response.data || []);
        } else {
          this.permissionTreeData = response.data || [];
        }
        this.permissionExpandedKeys = this.getAllPermissionKeys(this.permissionTreeData);
      } catch (error) {
        console.error('加载权限树失败:', error);
        this.$message.error('加载权限树失败');
      }
    },
    
    // 根据当前租户权限过滤权限树（仅租户用户使用）
    filterPermissionTreeByTenant(nodes) {
      if (this.tenantPermissions.length === 0) {
        return [];
      }
          
      const filterNodes = (items) => {
        return items.map(node => {
          // 如果节点的 value 在租户权限中，保留该节点
          if (this.tenantPermissions.includes(node.value)) {
            const filteredNode = { ...node };
            // 递归处理子节点
            if (node.children && node.children.length) {
              const filteredChildren = filterNodes(node.children);
              if (filteredChildren.length > 0) {
                filteredNode.children = filteredChildren;
              } else {
                delete filteredNode.children;
              }
            }
            return filteredNode;
          } 
          // 如果节点没有 value 但有子节点，检查子节点
          else if (node.children && node.children.length) {
            const filteredChildren = filterNodes(node.children);
            if (filteredChildren.length > 0) {
              return {
                ...node,
                children: filteredChildren
              };
            }
          }
          return null;
        }).filter(node => node !== null);
      };
          
      return filterNodes(nodes);
    },
    
    async loadTenantPermissions() {
      try {
        // 获取当前用户的租户ID
        const userInfo = JSON.parse(localStorage.getItem('aoxy-user') || '{}');
        const tenantId = userInfo.tenant_id;
        
        if (!tenantId) {
          console.warn('无法获取租户ID');
          this.tenantPermissions = [];
          return;
        }
        
        // 从后端获取租户权限
        const response = await this.$api.getTenantPermissions(tenantId);
        if (response.code === 200) {
          this.tenantPermissions = response.data || [];
          // 重新过滤权限树
          await this.loadPermissionTree();
        } else {
          this.tenantPermissions = [];
        }
      } catch (error) {
        console.error('获取租户权限失败:', error);
        this.tenantPermissions = [];
      }
    },
    
    getAllPermissionKeys(nodes) {
      const keys = [];
      const traverse = (items) => {
        items.forEach(item => {
          keys.push(item.value);
          if (item.children && item.children.length) {
            traverse(item.children);
          }
        });
      };
      traverse(nodes);
      return keys;
    },
    
    handleTableChange(pagination) {
      this.pagination.current = pagination.current;
      this.pagination.pageSize = pagination.pageSize;
      this.loadRoles();
    },
    
    async openCreateDialog() {
      // 先显示 loading
      const hideLoading = this.$message.loading('正在加载权限数据...', 0);
      
      try {
        // 重新加载可用权限（确保超管看到的是完整权限树）
        await this.loadAvailablePermissions();
        
        // 权限树加载完成后再设置对话框
        this.dialogTitle = '新增角色';
        this.currentRole = {};
        this.checkedPermissionKeys = [];
        this.dialogVisible = true;
        this.roleForm.resetFields();
        
        // 超管情况下清空租户选择
        if (this.isSuperAdmin) {
          this.$nextTick(() => {
            this.roleForm.setFieldsValue({ tenant_id: undefined });
          });
        }
        
        // 关闭 loading
        hideLoading();
      } catch (error) {
        hideLoading();
        console.error('[角色管理] 打开创建对话框失败:', error);
        this.$message.error('加载权限数据失败');
      }
    },
    
    async openEditDialog(row) {
      // 先显示 loading
      const hideLoading = this.$message.loading('正在加载权限数据...', 0);
      
      try {
        // 重新加载可用权限（后端会根据用户类型返回不同的结果）
        await this.loadAvailablePermissions();
        
        // 收集所有可用的权限值
        const availablePermissionValues = [];
        const collectPermissionValues = (nodes) => {
          nodes.forEach(node => {
            if (node.value) availablePermissionValues.push(node.value);
            if (node.children && node.children.length) {
              collectPermissionValues(node.children);
            }
          });
        };
        collectPermissionValues(this.permissionTreeData);
        
        console.log('[角色管理] 可用权限值:', availablePermissionValues);
        console.log('[角色管理] 角色原有权限:', row.permissions);
        
        // 权限树加载完成后再设置对话框
        this.dialogTitle = '编辑角色';
        this.currentRole = { ...row };
        
        // 过滤掉不在可用权限列表中的权限值（防止历史遗留权限导致验证失败）
        this.checkedPermissionKeys = (row.permissions || []).filter(key => 
          availablePermissionValues.includes(key)
        );
        
        console.log('[角色管理] 过滤后的权限:', this.checkedPermissionKeys);
        
        this.dialogVisible = true;
        
        // 等待 DOM 更新后再填充表单
        await this.$nextTick();
        
        // 填充表单数据
        this.roleForm.setFieldsValue({
          name: row.name,
          code: row.code
        });
        
        console.log('[角色管理] 编辑对话框打开成功，表单值已设置:', {
          name: row.name,
          code: row.code,
          permissions: this.checkedPermissionKeys.length,
          canEditPermissions: this.isSuperAdmin
        });
        
        // 关闭 loading
        hideLoading();
      } catch (error) {
        hideLoading();
        console.error('[角色管理] 打开编辑对话框失败:', error);
        this.$message.error('加载权限数据失败');
      }
    },
    
    closeDialog() {
      this.dialogVisible = false;
      this.roleForm.resetFields();
    },
    
    getFieldError(field) {
      const errors = this.roleForm.getFieldError(field);
      return errors ? errors[0] : '';
    },
    
    handlePermissionTreeExpand(expandedKeys) {
      this.permissionExpandedKeys = expandedKeys;
    },
    
    async saveRole() {
      try {
        const values = await this.roleForm.validateFields();
                
        console.log('[角色管理] 保存角色数据:', {
          name: values.name,
          code: values.code,
          permissionsCount: this.checkedPermissionKeys.length
        });
                
        const roleData = {
          name: values.name,
          code: values.code,
          permissions: this.checkedPermissionKeys
        };
            
        // 超级管理员需要指定 tenant_id
        if (this.isSuperAdmin) {
          roleData.tenant_id = values.tenant_id;
        }
            
        if (this.currentRole.id) {
          // 更新角色
          await this.$api.update_role(this.currentRole.id, roleData);
          this.$message.success('更新角色成功');
        } else {
          // 创建角色
          await this.$api.save_role(roleData);
          this.$message.success('创建角色成功');
        }
            
        this.closeDialog();
        await this.loadRoles();
      } catch (error) {
        if (error.errorFields) {
          return;
        }
        console.error('[角色管理] 保存角色失败:', error);
        this.$message.error(error.message || '保存角色失败');
      }
    },
    
    async deleteRole(record) {
      try {
        await this.$api.delete_role(record.id);
        this.$message.success('删除角色成功');
        await this.loadRoles();
      } catch (error) {
        console.error('删除角色失败:', error);
        this.$message.error(error.message || '删除角色失败');
      }
    },
    
    // 显示租户权限详情
    showTenantPermissions() {
      this.tenantPermissionsVisible = true;
    },
    
    // 显示全部权限详情（超管使用）
    showAllPermissions() {
      this.allPermissionsVisible = true;
    }
  }
}
</script>

<style scoped>
.role-management-container {
  padding: 20px;
}
</style>